import {z} from 'zod';
import policy from '../harness/native/customer-conversation-policy.json' with {type: 'json'};
import {NativeLiveTurnSchema, type NativeMessage} from './native-hermes-types.js';
import {NativeCommunicationProvenanceSchema, NativeCommunicationUpdateSchema} from './native-communication.js';
import {ChatAttachmentSchema} from './chat-attachments.js';

/** This projection controls which native conversation records reach customers. */
export const internalConversationKinds = new Set(policy.internalKinds);
const syntheticFlags = policy.syntheticFlags;
const taskHeader = '[Your active task list was preserved across context compression]';
const skillHeader = '[Skills pruned during compression — reload before acting on these tasks]';
function record(value: unknown): Record<string,unknown> | null {
  return value !== null && typeof value === 'object' && !Array.isArray(value) ? value as Record<string,unknown> : null;
}
function internal(row: Record<string,unknown>): boolean {
  return internalConversationKinds.has(String(row.display_kind ?? ''))
    || record(row.display_metadata)?.[policy.internalMetadataFlag] === true;
}

/** Hermes serializes native image parts as a trailing reference/placeholder pair.
 * Recognize that complete envelope only, never an @image mention in user prose.
 * Cache paths remain in native history; customers receive an attachment count.
 */
function customerImageMessage(text: string, original: Record<string,unknown> | undefined): {text: string; imageAttachmentCount: number} {
  const unchanged = {text,imageAttachmentCount:0};
  if (original?.content !== text) return unchanged;
  const lines = text.split('\n');
  let end = lines.length;
  while (end > 0 && lines[end - 1] === '[screenshot]') end--;
  const imageAttachmentCount = lines.length - end;
  if (imageAttachmentCount < 1 || imageAttachmentCount > 16 || end < imageAttachmentCount) return unchanged;
  const start = end - imageAttachmentCount;
  // These paths are minted by the native image attachment/cache owners. Do not
  // interpret arbitrary file directives, URLs, relative paths or code examples.
  const imageReference = /^@image:\/home\/hermes\/\.hermes\/(?:cache\/)?images\/[A-Za-z0-9_.-]+\.(?:png|jpe?g|gif|webp|bmp)$/i;
  if (lines.slice(start,end).some(line => !imageReference.test(line))) return unchanged;
  let fence: string | null = null;
  for (const line of lines.slice(0,start)) {
    const marker = /^ {0,3}(`{3,}|~{3,})/.exec(line)?.[1];
    if (!marker) continue;
    if (fence === null) fence=marker;
    else if (marker[0] === fence[0] && marker.length >= fence.length && line.trim() === marker) fence=null;
  }
  if (fence !== null) return unchanged;
  return {text:lines.slice(0,start).join('\n').trimEnd(),imageAttachmentCount};
}

/** Remove recognized machine-injected notices, retaining a preceding human input. */
export function customerUserText(text: string): string | null {
  let visible = text;
  for (const header of [taskHeader,skillHeader]) {
    const position = visible.indexOf(header);
    if (position === 0 || position > 0 && /\n\s*$/.test(visible.slice(0,position))) visible=visible.slice(0,position).trimEnd();
  }
  if (/^\s*\[(?:System:|IMPORTANT: Background process )/.test(visible)) return null;
  return visible.trim() ? visible : null;
}

export function customerMessages(value: unknown, rawValue: unknown): NativeMessage[] {
  const rows = z.array(z.record(z.string(),z.unknown())).parse(value);
  const raw = z.array(z.record(z.string(),z.unknown())).parse(rawValue);
  const originals = new Map(raw.filter(row => typeof row._row_id === 'number').map(row => [row._row_id,row]));
  return rows.flatMap(row => {
    if (row.role !== 'user' && row.role !== 'assistant' || internal(row)) return [];
    const original = originals.get(row.row_id);
    if (original && internal(original)) return [];
    if (typeof row.text !== 'string') return [];
    const metadata = record(row.display_metadata);
    const provenance=metadata?._eight_communication===undefined?null:NativeCommunicationProvenanceSchema.parse(metadata._eight_communication);
    const originalInput=row.role==='user'&&provenance?.producer==='user';
    const attachments=originalInput?(provenance.attachments??[]).map(({id,name,size,mediaType})=>ChatAttachmentSchema.parse({id,name,size,mediaType})):[];
    if (!originalInput && row.role === 'user' && customerUserText(row.text) === null) return [];
    const projected = row.role === 'user' && !originalInput ? customerImageMessage(row.text,original) : {text:row.text,imageAttachmentCount:0};
    const text = row.role === 'user' && !originalInput ? customerUserText(projected.text) : projected.text;
    if ((!text?.trim() && projected.imageAttachmentCount === 0 && attachments.length===0) || !originalInput && original && row.role === 'user' && syntheticFlags.some(flag => original[flag] === true)
      && text === row.text) return [];
    // Never forward model content, tool arguments/results, reasoning, or arbitrary sidecars.
    const result: NativeMessage = {role: row.role,text:text ?? ''};
    if(attachments.length)result.attachments=attachments;
    if(provenance)result.communication={producer:provenance.producer,requestId:provenance.request_id,turnId:provenance.turn_id,
      ...(provenance.agent_id===undefined?{}:{agentId:provenance.agent_id}),...(provenance.incomplete?{incomplete:true}:{}),
      ...(provenance.result_control_id?{resultControlId:provenance.result_control_id}:{}),
      ...(provenance.reply_to?{replyTo:provenance.reply_to}:{}),...(provenance.call_id?{callId:provenance.call_id}:{})};
    if (projected.imageAttachmentCount > 0) result.imageAttachmentCount=projected.imageAttachmentCount;
    if (typeof row.row_id === 'number') result.row_id=row.row_id;
    // Hermes's fixed-size logical identity survives carried-row compaction.
    // Only this verified metadata field may accompany the existing row ID.
    if (row.logical_id !== undefined) result.logical_id=z.string().regex(/^[a-f0-9]{64}$/).parse(row.logical_id);
    if (typeof row.timestamp === 'number' || typeof row.timestamp === 'string') result.timestamp=row.timestamp;
    if (typeof row.display_kind === 'string') result.display_kind=row.display_kind;
    if (typeof metadata?.delivered_to_model === 'boolean') result.display_metadata={delivered_to_model:metadata.delivered_to_model};
    return [result];
  });
}

export function customerLiveTurn(value: unknown): z.infer<typeof NativeLiveTurnSchema> | null {
  if (value === null) return null;
  const turn = NativeLiveTurnSchema.parse(value);
  const metadata = record(turn.display_metadata);
  const user = internal(turn) ? '' : customerUserText(turn.user) ?? '';
  const corrections = turn.corrections ?? [], offsets = turn.correction_offsets ?? [];
  if (corrections.length !== offsets.length) throw new Error('The live turn has mismatched correction positions.');
  const retained = corrections.flatMap((text,index) => {
    const visible=customerUserText(text);
    return visible === null ? [] : [{text:visible,offset:offsets[index]!}];
  });
  const {display_metadata: _metadata,...visibleTurn}=turn;
  return {...visibleTurn,user,corrections:retained.map(item => item.text),correction_offsets:retained.map(item => item.offset),
    ...(typeof metadata?.delivered_to_model === 'boolean' ? {display_metadata:{delivered_to_model:metadata.delivered_to_model}} : {})};
}

/** Only validated customer communication payloads accompany native invalidation events. */
export function customerEvents(value: unknown): Array<Record<string,unknown>> {
  return z.array(z.record(z.string(),z.unknown())).parse(value).map(row => {
    const projected: Record<string,unknown>={type:z.string().parse(row.type)};
    for (const key of ['seq','timestamp']) if (typeof row[key] === 'number') projected[key]=row[key];
    if(row.type==='communication.update')projected.payload=NativeCommunicationUpdateSchema.parse(row.payload);
    return projected;
  });
}
